/**
 * 连接事务 —— 对应技术设计 §5.4「从拖拽到关系图」。
 *
 * 三个阶段，状态机由 Relation.state 承载（`eom/relation.ts:71`）：
 *   preview —— 拖拽中最高分候选，画虚线，**不进 hasRelation 统计**
 *   active  —— 松手提交，装置真的搭上了
 *   broken  —— 拆除或约束失效
 *
 * preview 不算数这一点很关键：拖拽过程中经过某个端口不能被判定为"装好了"，
 * 否则 Mission 步骤会在手指还没松开时就通过。`assembly-graph.hasRelation`
 * 只统计 active（`:102`），这里只要保证 preview 不误写成 active。
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import { addRelation, listRelations, removeRelation } from "../eom/assembly-graph.js";
import type { Endpoint, Relation } from "../eom/relation.js";
import type { AnchorFacet } from "../eom/port.js";
import type { Bond } from "./action-grammar.js";
import { solveConstraints } from "./constraint-solver.js";
import type { SnapCandidate } from "./snap-controller.js";

const PREVIEW_RELATION_ID = "__preview__";

function toEndpoint(facet: AnchorFacet): Endpoint {
  if (facet.anchorType === "port" && facet.anchorId !== null) {
    return { objectId: facet.objectId, portId: facet.anchorId };
  }
  if (facet.anchorType === "slot" && facet.anchorId !== null) {
    return { objectId: facet.objectId, slotId: facet.anchorId };
  }
  return { objectId: facet.objectId };
}

/** 关系 id 用端点构造，保证同一对锚点重复连接不会产生两条关系。 */
export function relationIdFor(bond: Bond): string {
  const part = (facet: AnchorFacet): string =>
    facet.anchorId === null ? facet.objectId : `${facet.objectId}.${facet.anchorId}`;
  return `${bond.relation}:${part(bond.from)}:${part(bond.to)}`;
}

function buildRelation(bond: Bond, id: string, state: Relation["state"], at: number): Relation {
  return {
    id,
    type: bond.relation,
    from: toEndpoint(bond.from),
    to: toEndpoint(bond.to),
    state,
    constraints: [{ kind: bond.constraint }],
    createdAt: at,
    createdBy: "user",
  };
}

/** 拖拽中：把最高分候选写成 preview 关系（先清掉上一帧的 preview）。 */
export function setPreview(graph: AssemblyGraph, candidate: SnapCandidate | null, at: number): AssemblyGraph {
  const cleared = clearPreview(graph);
  if (!candidate) return cleared;
  return addRelation(cleared, buildRelation(candidate.bond, PREVIEW_RELATION_ID, "preview", at));
}

export function clearPreview(graph: AssemblyGraph): AssemblyGraph {
  return removeRelation(graph, PREVIEW_RELATION_ID);
}

export function getPreview(graph: AssemblyGraph): Relation | undefined {
  return graph.relations[PREVIEW_RELATION_ID];
}

export interface CommitResult {
  graph: AssemblyGraph;
  relation: Relation | null;
  /** 已存在同一条关系时为 true，调用方据此不重复发事件 */
  duplicated: boolean;
}

/**
 * 松手提交：preview → active，并立刻跑一次约束求解让下游器材跟上。
 *
 * 吸附对齐由 `solveConstraints` 完成 —— 提交前先把主动侧对象移到位，
 * 再让约束把接收侧摆好，两步都走同一套 `toWorld`，不会出现
 * 「画面看着连上了但锚点差几像素」（红线 2）。
 */
export function commit(graph: AssemblyGraph, candidate: SnapCandidate, at: number): CommitResult {
  const id = relationIdFor(candidate.bond);
  const cleared = clearPreview(graph);

  const existing = listRelations(cleared).find(
    (relation) => relation.id === id && relation.state === "active",
  );
  if (existing) {
    return { graph: cleared, relation: existing, duplicated: true };
  }

  // 改插别处：把这两个端口上的旧连接先拆掉。
  // 端口是独占的，同一个端口不能既插在水槽又插在瓶口；不拆的话会出现两条互相矛盾的关系。
  let base = cleared;
  for (const endpoint of [toEndpoint(candidate.bond.from), toEndpoint(candidate.bond.to)]) {
    if (endpoint.portId === undefined) continue;
    for (const existing of listRelations(base)) {
      if (existing.state !== "active") continue;
      const touches = [existing.from, existing.to].some(
        (side) => side.objectId === endpoint.objectId && side.portId === endpoint.portId,
      );
      if (touches) base = removeRelation(base, existing.id);
    }
  }

  const relation = buildRelation(candidate.bond, id, "active", at);
  const withRelation = addRelation(base, relation);
  const solved = solveConstraints(withRelation, candidate.bond.from.objectId);
  return { graph: solved, relation, duplicated: false };
}

/** 拆除：active → 移除。撤除装置（§15.1 第 8 步）与「拖开就断开」都走这里。 */
export function detach(graph: AssemblyGraph, relationId: string): AssemblyGraph {
  return removeRelation(graph, relationId);
}

/**
 * 把某件器材身上所有 active 关系断开。学生把已连接的器材拖走时调用 ——
 * §14.1「错误操作可发生，但要可恢复，不让学生卡死」：粘死了才是卡死。
 */
export function detachAll(graph: AssemblyGraph, objectId: string): { graph: AssemblyGraph; removed: Relation[] } {
  const removed = listRelations(graph).filter(
    (relation) =>
      relation.state === "active" &&
      (relation.from.objectId === objectId || relation.to.objectId === objectId),
  );
  let next = graph;
  for (const relation of removed) {
    next = removeRelation(next, relation.id);
  }
  return { graph: next, removed };
}
